import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  Package,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Image as ImageIcon,
  Brain,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { getAdminProducts, getProductCategories, getAllProductIds, embedProduct } from '../../services/adminService';
import { logger } from '../../lib/logger';

interface Product {
  id: string;
  slug: string;
  name: string;
  sku: string;
  product_type: 'single' | 'bundle' | 'kit';
  primary_category: string | null;
  category_path: string | null;
  price_gross: number | null;
  currency: string | null;
  is_active: boolean;
  image_url: string | null;
  last_embedded_at: string | null;
  product_media: { id: string; url: string; type: string; sort_order: number }[];
}

interface EmbedProgress {
  isRunning: boolean;
  currentIndex: number;
  totalCount: number;
  currentProduct: string;
  successCount: number;
  errorCount: number;
  errors: string[];
}

export function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedProgress, setEmbedProgress] = useState<EmbedProgress>({
    isRunning: false,
    currentIndex: 0,
    totalCount: 0,
    currentProduct: '',
    successCount: 0,
    errorCount: 0,
    errors: [],
  });

  const pageSize = 20;

  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getAdminProducts(
        search || undefined,
        categoryFilter || undefined,
        typeFilter || undefined,
        pageSize,
        (page - 1) * pageSize
      );
      setProducts(result.products);
      setTotal(result.total);
    } catch (error) {
      logger.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, search, categoryFilter, typeFilter]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    getProductCategories().then(setCategories).catch(logger.error);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadProducts();
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setTypeFilter('');
    setPage(1);
  };

  const hasActiveFilters = search || categoryFilter || typeFilter;
  const totalPages = Math.ceil(total / pageSize);

  const handleEmbedAllProducts = async () => {
    try {
      const allProducts = await getAllProductIds();

      if (allProducts.length === 0) {
        return;
      }

      setEmbedProgress({
        isRunning: true,
        currentIndex: 0,
        totalCount: allProducts.length,
        currentProduct: allProducts[0].name,
        successCount: 0,
        errorCount: 0,
        errors: [],
      });

      for (let i = 0; i < allProducts.length; i++) {
        const product = allProducts[i];

        setEmbedProgress(prev => ({
          ...prev,
          currentIndex: i + 1,
          currentProduct: product.name,
        }));

        try {
          await embedProduct(product.id);
          setEmbedProgress(prev => ({
            ...prev,
            successCount: prev.successCount + 1,
          }));
        } catch (error) {
          const errorMsg = `${product.name}: ${error instanceof Error ? error.message : 'Fehler'}`;
          setEmbedProgress(prev => ({
            ...prev,
            errorCount: prev.errorCount + 1,
            errors: [...prev.errors.slice(-4), errorMsg], // Keep last 5 errors
          }));
        }

        // Small delay to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setEmbedProgress(prev => ({
        ...prev,
        isRunning: false,
      }));

      // Reload products to show updated embedding status
      loadProducts();
    } catch (error) {
      logger.error('Error loading products for embedding:', error);
      setEmbedProgress(prev => ({
        ...prev,
        isRunning: false,
        errors: [...prev.errors, 'Fehler beim Laden der Produkte'],
      }));
    }
  };

  const closeEmbedModal = () => {
    if (!embedProgress.isRunning) {
      setShowEmbedModal(false);
      setEmbedProgress({
        isRunning: false,
        currentIndex: 0,
        totalCount: 0,
        currentProduct: '',
        successCount: 0,
        errorCount: 0,
        errors: [],
      });
    }
  };

  const getProductImage = (product: Product) => {
    if (product.image_url) return product.image_url;
    // Sort by sort_order to get the correct cover image
    const sortedMedia = [...(product.product_media || [])].sort((a, b) => a.sort_order - b.sort_order);
    const firstImage = sortedMedia.find((m) => m.type === 'image');
    return firstImage?.url || null;
  };

  const formatPrice = (price: number | null, currency: string | null) => {
    if (price === null) return '-';
    // Handle invalid currency codes (e.g., '€' instead of 'EUR')
    let currencyCode = currency || 'EUR';
    if (currencyCode === '€') currencyCode = 'EUR';
    if (currencyCode === '$') currencyCode = 'USD';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currencyCode,
    }).format(price);
  };

  const productTypeLabels: Record<string, string> = {
    single: 'Einzelprodukt',
    bundle: 'Bundle',
    kit: 'Kit',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Produktkatalog</h1>
          <p className="text-slate-400 mt-1">{total} Produkte insgesamt</p>
        </div>
        <button
          onClick={() => {
            setShowEmbedModal(true);
            handleEmbedAllProducts();
          }}
          disabled={embedProgress.isRunning}
          className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${embedProgress.isRunning ? 'animate-spin' : ''}`} />
          Alle Produkte embedden
        </button>
      </div>

      {/* Embed Progress Modal */}
      {showEmbedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <Brain className="w-6 h-6 text-violet-400" />
                  KI-Wissen aktualisieren
                </h2>
                {!embedProgress.isRunning && (
                  <button
                    onClick={closeEmbedModal}
                    className="p-2 text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Progress */}
              <div className="space-y-4">
                {/* Progress Bar */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Fortschritt</span>
                    <span className="text-sm text-white font-medium">
                      {embedProgress.currentIndex} / {embedProgress.totalCount}
                    </span>
                  </div>
                  <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300"
                      style={{
                        width: embedProgress.totalCount > 0
                          ? `${(embedProgress.currentIndex / embedProgress.totalCount) * 100}%`
                          : '0%',
                      }}
                    />
                  </div>
                </div>

                {/* Current Product */}
                {embedProgress.isRunning && (
                  <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl">
                    <Loader2 className="w-5 h-5 text-violet-400 animate-spin flex-shrink-0" />
                    <span className="text-sm text-slate-300 truncate">
                      {embedProgress.currentProduct}
                    </span>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-emerald-400">Erfolgreich</span>
                    </div>
                    <span className="text-2xl font-bold text-emerald-400">
                      {embedProgress.successCount}
                    </span>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-red-400">Fehler</span>
                    </div>
                    <span className="text-2xl font-bold text-red-400">
                      {embedProgress.errorCount}
                    </span>
                  </div>
                </div>

                {/* Error List */}
                {embedProgress.errors.length > 0 && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <p className="text-sm font-medium text-red-400 mb-2">Letzte Fehler:</p>
                    <div className="space-y-1">
                      {embedProgress.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-300 truncate">{err}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completion Message */}
                {!embedProgress.isRunning && embedProgress.totalCount > 0 && (
                  <div className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl text-center">
                    <CheckCircle className="w-8 h-8 text-violet-400 mx-auto mb-2" />
                    <p className="text-white font-medium">Embedding abgeschlossen!</p>
                    <p className="text-sm text-slate-400 mt-1">
                      {embedProgress.successCount} Produkte erfolgreich aktualisiert
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {!embedProgress.isRunning && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeEmbedModal}
                    className="px-6 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-colors font-medium"
                  >
                    Schließen
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name oder SKU suchen..."
              className="w-full pl-12 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-colors ${
              hasActiveFilters
                ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filter
            {hasActiveFilters && <span className="w-2 h-2 bg-orange-500 rounded-full" />}
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium"
          >
            Suchen
          </button>
        </form>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-800 flex flex-wrap gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Kategorie</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Alle</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Produkttyp</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Alle</option>
                <option value="single">Einzelprodukt</option>
                <option value="bundle">Bundle</option>
                <option value="kit">Kit</option>
              </select>
            </div>
            {hasActiveFilters && (
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                  Filter zurücksetzen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Products Grid */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Keine Produkte gefunden</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Produkt
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      SKU
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Kategorie
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Typ
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Preis
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Status
                    </th>
                    <th className="text-center text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      KI
                    </th>
                    <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-slate-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {getProductImage(product) ? (
                            <img
                              src={getProductImage(product)!}
                              alt=""
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                              <ImageIcon className="w-6 h-6 text-slate-500" />
                            </div>
                          )}
                          <span className="font-medium text-white">{product.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-400 font-mono">{product.sku}</td>
                      <td className="px-6 py-4 text-sm text-slate-300">
                        {product.primary_category || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 bg-slate-700 text-slate-300 rounded-lg text-xs">
                          {productTypeLabels[product.product_type] || product.product_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-white font-medium">
                        {formatPrice(product.price_gross, product.currency)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            product.is_active
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}
                        >
                          {product.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {product.last_embedded_at ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-violet-500/20 rounded-lg" title={`Embedded: ${new Date(product.last_embedded_at).toLocaleDateString('de-DE')}`}>
                            <Brain className="w-4 h-4 text-violet-400" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-slate-700/50 rounded-lg" title="Nicht embedded">
                            <Brain className="w-4 h-4 text-slate-500" />
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end">
                          <Link
                            to={`/admin/products/${product.slug}`}
                            className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            Bearbeiten
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-slate-800">
              {products.map((product) => (
                <div key={product.id} className="p-4">
                  <div className="flex items-start gap-4">
                    {getProductImage(product) ? (
                      <img
                        src={getProductImage(product)!}
                        alt=""
                        className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-slate-700 rounded-xl flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-8 h-8 text-slate-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-white truncate">{product.name}</p>
                          <p className="text-sm text-slate-400 font-mono">{product.sku}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {product.last_embedded_at ? (
                            <span className="w-6 h-6 bg-violet-500/20 rounded flex items-center justify-center" title="KI embedded">
                              <Brain className="w-3.5 h-3.5 text-violet-400" />
                            </span>
                          ) : (
                            <span className="w-6 h-6 bg-slate-700/50 rounded flex items-center justify-center" title="Nicht embedded">
                              <Brain className="w-3.5 h-3.5 text-slate-500" />
                            </span>
                          )}
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              product.is_active
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-slate-700 text-slate-400'
                            }`}
                          >
                            {product.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-white font-medium">
                          {formatPrice(product.price_gross, product.currency)}
                        </span>
                        <span className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded text-xs">
                          {productTypeLabels[product.product_type]}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/admin/products/${product.slug}`}
                    className="flex items-center justify-center gap-2 w-full mt-4 px-4 py-2 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Bearbeiten
                  </Link>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
                <p className="text-sm text-slate-400">
                  Seite {page} von {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="p-2 bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="p-2 bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
